/**
 * Runtime polyfills for Chromium ~90 (Samsung S8 Chrome WebView baseline).
 *
 * Side-effect import — `import "@/lib/polyfills/legacy-chromium"` MUST be the
 * first line of `src/main.tsx` so every chunk loaded after bootstrap can
 * rely on these methods existing.
 *
 * Each polyfill is feature-detected and idempotent. Bundle cost: ~0.5 kB
 * minified, ~0.3 kB after gzip (all branches dead-code-eliminate when
 * `globalThis` has the API).
 *
 * Reference: features audit at
 * `frontend/.parity-loop/android-s8/chromium-features-audit.md`.
 */

type CryptoWithRandomUUID = Crypto & { randomUUID(): string };

interface ObjectWithHasOwn {
  hasOwn(target: object, key: PropertyKey): boolean;
}

interface ArrayLikeAt<T> {
  at(index: number): T | undefined;
}

function installObjectHasOwn(): void {
  if (typeof (Object as Partial<ObjectWithHasOwn>).hasOwn === "function") {
    return;
  }
  Object.defineProperty(Object, "hasOwn", {
    configurable: true,
    value: function hasOwn(target: object, key: PropertyKey): boolean {
      if (target === null || target === undefined) {
        throw new TypeError(
          `Object.hasOwn called on ${String(target)}; expected an object`,
        );
      }
      return Object.prototype.hasOwnProperty.call(Object(target), key);
    },
    writable: true,
  });
}

function installArrayAt(): void {
  const proto = Array.prototype as unknown as Partial<ArrayLikeAt<unknown>>;
  if (typeof proto.at === "function") {
    return;
  }
  const at = function at<T>(this: ArrayLike<T>, index: number): T | undefined {
    const len = this.length >>> 0;
    const relative = Math.trunc(index) || 0;
    const idx = relative < 0 ? len + relative : relative;
    if (idx < 0 || idx >= len) return undefined;
    return this[idx];
  };
  for (const ctor of [
    Array,
    String,
    Int8Array,
    Uint8Array,
    Int16Array,
    Uint16Array,
    Int32Array,
    Uint32Array,
    Float32Array,
    Float64Array,
  ] as Array<{ prototype: object } | undefined>) {
    if (!ctor) continue;
    const target = ctor.prototype as Record<string, unknown>;
    if (typeof target["at"] === "function") continue;
    Object.defineProperty(target, "at", {
      configurable: true,
      value: at,
      writable: true,
    });
  }
}

function installCryptoRandomUUID(): void {
  const cryptoRef = (globalThis as { crypto?: Crypto }).crypto;
  if (!cryptoRef) return;
  if (
    typeof (cryptoRef as Partial<CryptoWithRandomUUID>).randomUUID ===
    "function"
  ) {
    return;
  }
  const getRandomValues = cryptoRef.getRandomValues?.bind(cryptoRef);
  if (!getRandomValues) return;
  const randomUUID = function randomUUID(): string {
    // RFC 4122 v4 — same shape Chromium's native implementation produces.
    const bytes = getRandomValues(new Uint8Array(16));
    bytes[6] = ((bytes[6] ?? 0) & 0x0f) | 0x40;
    bytes[8] = ((bytes[8] ?? 0) & 0x3f) | 0x80;
    const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, "0"));
    return `${hex.slice(0, 4).join("")}-${hex.slice(4, 6).join("")}-${hex
      .slice(6, 8)
      .join("")}-${hex.slice(8, 10).join("")}-${hex.slice(10, 16).join("")}`;
  };
  try {
    Object.defineProperty(cryptoRef, "randomUUID", {
      configurable: true,
      value: randomUUID,
      writable: true,
    });
  } catch {
    // Some runtimes (older WebView) freeze the crypto object — fall through
    // and let call-sites that already guard `crypto.randomUUID` keep their
    // own UUID fallback path.
  }
}

function installStructuredClone(): void {
  const target = globalThis as { structuredClone?: <T>(v: T) => T };
  if (typeof target.structuredClone === "function") return;
  // JSON-based fallback is sufficient for the only call sites we control
  // (plain serializable state copies). Refs, Maps, Sets, Dates etc. are not
  // currently passed through structuredClone in this codebase.
  target.structuredClone = function structuredCloneShim<T>(value: T): T {
    if (value === undefined) return value;
    return JSON.parse(JSON.stringify(value)) as T;
  };
}

installObjectHasOwn();
installArrayAt();
installCryptoRandomUUID();
installStructuredClone();

export {};
